import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ClipLoader, PulseLoader } from 'react-spinners';
import moment from 'moment';
import { TrashIcon } from '@heroicons/react/24/outline';

import { useAlert } from 'context/AlertContext';
import {
  getEventMessages,
  createEventMessage,
  deleteEventMessage,
} from '../../../redux/actions/events';

import { cx } from './atoms';

export default function UpdatesTab({ event, user, isPromoter }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { confirm } = useAlert();
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);

  const eventId = event?.id;
  const slice = useSelector(
    (state) => state.events.eventMessages.byEventId[eventId]
  );
  const messages = slice?.messages || [];
  const loading = slice?.loading || false;

  const promoterAvatar =
    event?.Promoter?.avatar || event?.promoterAvatar || user?.avatar;
  const promoterName =
    event?.Promoter?.username || event?.promoterName || '';
  const confirmedBrands = Number(event?.bookedSpaces ?? event?.confirmedSpaces) || 0;

  const submit = async () => {
    if (!eventId) return;
    const trimmed = draft.trim();
    if (!trimmed) return;
    setPosting(true);
    try {
      await dispatch(createEventMessage(eventId, trimmed));
      setDraft('');
      dispatch(getEventMessages(eventId));
    } catch (err) {
      console.error('Failed to post update:', err);
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (messageId) => {
    const ok = await confirm({
      type: 'confirm',
      title: t('dashboard.confirm_delete_title', 'Delete this update?'),
      message: t(
        'dashboard.confirm_delete_message',
        'This update will be removed for everyone. This cannot be undone.'
      ),
      confirmLabel: t('common.delete', 'Delete'),
      confirmIntent: 'danger',
      cancelLabel: t('common.cancel', 'Cancel'),
    });
    if (!ok) return;
    try {
      await dispatch(deleteEventMessage(messageId, eventId));
    } catch (err) {
      console.error('Failed to delete message:', err);
    }
  };

  if (loading && !messages.length) {
    return (
      <div className="flex justify-center items-center py-20">
        <PulseLoader color="#FFBD59" size={12} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto w-full">
      {isPromoter && (
        <div className="rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-4 mb-5">
          <div className="flex items-start gap-3">
            {promoterAvatar ? (
              <img
                src={promoterAvatar}
                className="w-9 h-9 rounded-full object-cover shrink-0 bg-gray-100"
                alt=""
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gray-100 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('dashboard.em.composer_placeholder')}
                rows={draft ? 3 : 1}
                className="w-full resize-none border-0 outline-none text-sm text-gray-900 placeholder-gray-400 bg-transparent"
              />
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                <span className="text-[11px] text-gray-400">
                  {t('dashboard.em.composer_sent_to', { count: confirmedBrands })}
                </span>
                <button
                  type="button"
                  onClick={submit}
                  disabled={!draft.trim() || posting}
                  className={cx(
                    'px-4 py-1.5 text-sm font-semibold rounded-lg transition',
                    draft.trim() && !posting
                      ? 'bg-brand text-white hover:bg-brand-hover'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  )}
                >
                  {posting ? (
                    <ClipLoader color="#fff" size={14} />
                  ) : (
                    t('dashboard.em.composer_post')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-gray-900">
          {t('dashboard.em.timeline')}
        </h3>
        <span className="text-xs text-gray-400">
          {t('dashboard.em.timeline_count', { count: messages.length })}
        </span>
      </div>

      {messages.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-white ring-1 ring-black/5">
          <div className="w-12 h-12 rounded-full bg-brand-soft mx-auto mb-3 flex items-center justify-center">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F0A830" strokeWidth="2">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
          </div>
          <p className="text-sm font-semibold text-gray-900">
            {t('dashboard.em.empty_updates_title')}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {isPromoter
              ? t('dashboard.em.empty_updates_promoter')
              : t('dashboard.em.empty_updates_brand')}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {messages.map((m) => (
            <li
              key={m.id}
              className={cx(
                'rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-4 transition hover:shadow',
                m.pinned && 'ring-amber-200 bg-brand-softer'
              )}
            >
              <div className="flex items-start gap-3">
                {promoterAvatar ? (
                  <img
                    src={promoterAvatar}
                    className="w-8 h-8 rounded-full object-cover shrink-0 bg-gray-100"
                    alt=""
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-100 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {promoterName && (
                      <span className="text-sm font-semibold text-gray-900">
                        {promoterName}
                      </span>
                    )}
                    <span className="text-xs text-gray-400">
                      · {moment(m.createdAt).fromNow()}
                    </span>
                    {m.pinned && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800">
                        {t('dashboard.em.pinned')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                    {m.message}
                  </p>
                </div>
                {isPromoter && (
                  <button
                    type="button"
                    onClick={() => handleDelete(m.id)}
                    className="text-gray-300 hover:text-rose-500 p-1 transition"
                    title={t('dashboard.em.delete')}
                    aria-label={t('dashboard.em.delete')}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
